<p-dialog header="Change Email" [(visible)]="showEmailChangeDialog" [modal]="true" styleClass="w-full max-w-md" [draggable]="false" [resizable]="false">
    <div class="space-y-4">
        <p>Enter your new email address below. We will send a verification link to your new email.</p>
        <input type="email" [(ngModel)]="newEmail" class="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="New Email Address" [disabled]="emailChangeLoading"/>
        <div *ngIf="emailChangeError" class="text-red-500 text-sm mt-2">{{ emailChangeError }}</div>
    </div>
    <ng-template pTemplate="footer">
        <button pButton type="button" label="Cancel" (click)="showEmailChangeDialog=false" class="p-button-text" [disabled]="emailChangeLoading"></button>
        <button pButton type="button" [label]="emailChangeLoading ? 'Sending...' : 'Send Verification Email'" (click)="requestEmailChange()" [disabled]="!newEmail || emailChangeLoading"></button>
    </ng-template>
</p-dialog>

<div class="space-y-4 md:space-y-6 px-2 md:px-0">
    <p-toast/>
    <h2 class="text-xl md:text-2xl font-bold text-gray-900 px-2 md:px-0">Profile Settings</h2>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
        <!-- Personal Information Form -->
        <div class="bg-white rounded-xl shadow-sm p-4 md:p-6">
            <h3 class="text-base md:text-lg font-semibold text-gray-900 mb-4 md:mb-6">Personal Information</h3>
            <form [formGroup]="profileForm" (ngSubmit)="onProfileUpdate()" class="space-y-3 md:space-y-4">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    <div>
                        <label class="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">First Name</label>
                        <input type="text" formControlName="firstName" class="w-full px-3 md:px-4 py-2 md:py-2 text-sm md:text-base text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" [class.border-red-500]="profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched"
                        />
                    </div>
                    <div>
                        <label class="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Last Name</label>
                        <input type="text" formControlName="lastName" class="w-full px-3 md:px-4 py-2 md:py-2 text-sm md:text-base text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" [class.border-red-500]="profileForm.get('lastName')?.invalid && profileForm.get('lastName')?.touched"
                        />
                    </div>
                </div>

                <div>
                    <label class="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Email</label>
                    <div class="flex items-center gap-4">
                        <input type="email" formControlName="email" class="w-full px-3 md:px-4 py-2 md:py-2 text-sm md:text-base border border-gray-300 rounded-lg bg-gray-50 text-gray-500" readonly/>
                        <button type="button" (click)="showEmailChangeDialog = true" class="bg-amber-500 text-white px-4 py-2 rounded-lg">Edit</button>
                    </div>
                </div>

                <div>
                    <label class="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Phone</label>
                    <input type="tel" formControlName="phone" class="w-full px-3 md:px-4 py-2 md:py-2 text-sm md:text-base text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" [class.border-red-500]="profileForm.get('phone')?.invalid && profileForm.get('phone')?.touched"
                    />
                </div>

                <div>
                    <label class="block text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Investment Goals</label>
                    <p-select [options]="investmentGoalOptions" formControlName="investmentGoals" optionValue="value" optionLabel="label" placeholder="Select Investment Goals"  class="w-full px-3 md:px-3 py-1 md:py-1 text-sm md:text-base text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" [class.border-red-500]="profileForm.get('investmentGoals')?.invalid && profileForm.get('investmentGoals')?.touched" />
                </div>

                <button type="submit" class="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-2.5 md:py-3 rounded-lg text-sm md:text-base font-semibold hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" [disabled]="profileLoading || profileForm.invalid">
            {{ profileLoading ? 'Updating...' : 'Update Profile' }}
          </button>
            </form>
        </div>

        <!-- Account Information -->
        <div class="bg-white rounded-xl shadow-sm p-4 md:p-6">
            <h3 class="text-base md:text-lg font-semibold text-gray-900 mb-4 md:mb-6">Account Information</h3>
            <div class="space-y-3 md:space-y-4">
                <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-2 md:py-3 border-b border-gray-200 gap-1 sm:gap-0">
                    <span class="text-xs md:text-sm text-gray-600">Member Since</span>
                    <span class="text-xs md:text-sm text-gray-900 font-medium">{{ profileForm.get('createdAt')?.value | date: 'mediumDate' }}</span>
                </div>
                <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-2 md:py-3 border-b border-gray-200 gap-1 sm:gap-0">
                    <span class="text-xs md:text-sm text-gray-600">Account Status</span>
                    <span class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs md:text-sm font-medium w-fit">Active</span>
                </div>
                <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-2 md:py-3 border-b border-gray-200 gap-1 sm:gap-0">
                    <span class="text-xs md:text-sm text-gray-600">Last Login</span>
                    <span class="text-xs md:text-sm text-gray-900 font-medium">{{ getFormattedLastLogin() }}</span>
                </div>
                <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-2 md:py-3 gap-1 sm:gap-0">
                    <span class="text-xs md:text-sm text-gray-600">Account Type</span>
                    <span class="text-xs md:text-sm text-gray-900 font-medium">Premium Investor</span>
                </div>
            </div>
        </div>
    </div>

    <!-- Account Security Section -->
    <div class="bg-white rounded-xl shadow-sm p-4 md:p-6" *ngIf="false">
        <h3 class="text-base md:text-lg font-semibold text-gray-900 mb-4 md:mb-6">Account Security</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div class="space-y-3 md:space-y-4">
                <!-- Account Verification -->
                <div class="flex items-center justify-between p-3 md:p-4 bg-green-50 rounded-lg">
                    <div class="flex items-center space-x-2 md:space-x-3">
                        <i-lucide name="shield" class="h-4 w-4 md:h-5 md:w-5 text-green-600"></i-lucide>
                        <span class="text-xs md:text-sm text-gray-700">Account Verification</span>
                    </div>
                    <i-lucide name="check-circle" class="h-4 w-4 md:h-5 md:w-5 text-green-600"></i-lucide>
                </div>

                <!-- Payment Methods -->
                <div class="flex items-center justify-between p-3 md:p-4 bg-green-50 rounded-lg">
                    <div class="flex items-center space-x-2 md:space-x-3">
                        <i-lucide name="credit-card" class="h-4 w-4 md:h-5 md:w-5 text-green-600"></i-lucide>
                        <span class="text-xs md:text-sm text-gray-700">Payment Methods</span>
                    </div>
                    <i-lucide name="check-circle" class="h-4 w-4 md:h-5 md:w-5 text-green-600"></i-lucide>
                </div>
            </div>

            <div class="space-y-3 md:space-y-4">
                <!-- Two-Factor Authentication -->
                <div class="flex items-center justify-between p-3 md:p-4 bg-yellow-50 rounded-lg">
                    <div class="flex items-center space-x-2 md:space-x-3">
                        <i-lucide name="shield" class="h-4 w-4 md:h-5 md:w-5 text-yellow-600"></i-lucide>
                        <span class="text-xs md:text-sm text-gray-700">Two-Factor Auth</span>
                    </div>
                    <button class="text-xs md:text-sm text-amber-600 hover:text-amber-700 font-medium px-2 py-1 rounded">Setup</button>
                </div>

                <!-- Document Upload -->
                <div class="flex items-center justify-between p-3 md:p-4 bg-gray-50 rounded-lg">
                    <div class="flex items-center space-x-2 md:space-x-3">
                        <i-lucide name="file-text" class="h-4 w-4 md:h-5 md:w-5 text-gray-600"></i-lucide>
                        <span class="text-xs md:text-sm text-gray-700">Document Upload</span>
                    </div>
                    <button class="text-xs md:text-sm text-amber-600 hover:text-amber-700 font-medium px-2 py-1 rounded">Upload</button>
                </div>
            </div>
        </div>
    </div>
</div>
